import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { hasDraft, clearDraft } from '@/hooks/useLocalStorage';
import { discardDraftUploads } from '@/lib/pendingUploads';
import { useAuth } from '@/contexts/AuthContext';

const NEW_DRAFT_KEY = 'orcazap_draft_new';
const EDIT_DRAFT_PREFIX = 'orcazap_draft_edit_';

// Helper to find any edit draft in localStorage
function findEditDraft(): { key: string; quoteId: string } | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(EDIT_DRAFT_PREFIX)) {
        const quoteId = key.replace(EDIT_DRAFT_PREFIX, '');
        const item = localStorage.getItem(key);
        if (item) {
          const parsed = JSON.parse(item);
          // Check if it has meaningful data
          if (parsed?.clientName || (parsed?.items && parsed.items.length > 0)) {
            return { key, quoteId };
          }
        }
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}

// Helper to clear edit draft
function clearEditDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error clearing edit draft "${key}":`, error);
  }
}

type DraftType = 'new' | 'edit' | null;

export function DraftIndicator() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [draftType, setDraftType] = useState<DraftType>(null);
  const [editDraftInfo, setEditDraftInfo] = useState<{ key: string; quoteId: string } | null>(null);

  // Check for drafts on mount and location change
  useEffect(() => {
    const checkDrafts = () => {
      // Visitors should not see draft indicator (they don't have persistent drafts)
      if (!user) {
        setDraftType(null);
        setEditDraftInfo(null);
        return;
      }
      
      // Hide if already on new quote page
      if (location.pathname === '/quotes/new') {
        setDraftType(null);
        setEditDraftInfo(null);
        return;
      }

      // Hide if on the specific edit page for the draft
      const editDraft = findEditDraft();
      if (editDraft && location.pathname === `/quotes/${editDraft.quoteId}`) {
        setDraftType(null);
        setEditDraftInfo(null);
        return;
      }

      // Check for new quote draft first
      if (hasDraft(NEW_DRAFT_KEY)) {
        setDraftType('new');
        setEditDraftInfo(null);
        return;
      }

      // Check for edit draft
      if (editDraft) {
        setDraftType('edit');
        setEditDraftInfo(editDraft);
        return;
      }

      setDraftType(null);
      setEditDraftInfo(null);
    };
    
    checkDrafts();
    
    // Listen for storage changes (in case draft is cleared from another tab or component)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === NEW_DRAFT_KEY || e.key?.startsWith(EDIT_DRAFT_PREFIX)) {
        checkDrafts();
      }
    };
    
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [location.pathname, user]);

  const handleContinue = () => {
    if (draftType === 'new') {
      navigate('/quotes/new');
    } else if (draftType === 'edit' && editDraftInfo) {
      navigate(`/quotes/${editDraftInfo.quoteId}`);
    }
  };

  /**
   * ESTE é o gesto que significa "desisti do documento" — e por isso é aqui,
   * e só aqui, que as fotos que o rascunho já subiu para o Storage são
   * apagadas.
   *
   * Sair da tela NÃO conta: o rascunho fica autossalvo e este mesmo indicador
   * oferece "Continuar orçamento". Apagar as fotos ao desmontar a página
   * devolveria o usuário a um rascunho cheio de URLs mortas.
   *
   * Só sai o que o formulário subiu (`pendingUploads`); foto de item vindo do
   * CATÁLOGO pertence ao produto e sobrevive ao documento. Ver
   * `src/lib/pendingUploads.ts`.
   */
  const handleDiscard = (e: React.MouseEvent) => {
    e.stopPropagation();
    const key = draftType === 'new' ? NEW_DRAFT_KEY : editDraftInfo?.key ?? null;
    // Lê ANTES de limpar — depois do `removeItem` não há mais de onde tirar os
    // caminhos, e o arquivo ficaria órfão para sempre.
    if (key) {
      try {
        void discardDraftUploads(localStorage.getItem(key));
      } catch (error) {
        console.error('Error reading draft before discard:', error);
      }
    }
    if (draftType === 'new') {
      clearDraft(NEW_DRAFT_KEY);
    } else if (draftType === 'edit' && editDraftInfo) {
      clearEditDraft(editDraftInfo.key);
    }
    setDraftType(null);
    setEditDraftInfo(null);
  };

  if (!draftType) return null;

  const label = draftType === 'new' ? 'Continuar orçamento' : 'Continuar edição';

  return (
    <div
      // Same lift as the FAB — clears BottomNavigation plus any Android/iOS
      // gesture-bar safe-area inset.
      style={{
        bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))',
      }}
      className="fixed left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div 
        onClick={handleContinue}
        className="flex items-center gap-3 bg-card border border-border shadow-lg rounded-full px-4 py-2.5 cursor-pointer hover:bg-accent transition-colors"
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
          <FileText className="h-4 w-4 text-primary" />
        </div>
        <span className="text-sm font-medium">{label}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 rounded-full hover:bg-destructive/10 hover:text-destructive -mr-1"
          onClick={handleDiscard}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
